import { computed } from "vue";

import { useEditorStore } from "@/stores/editor_store";
import { useMonitorSize } from "./monitor_size";

const THRESHOLD_SCALE = 1.0;
const MAXIMUM_SCALE = 2;
let DEFAULT_CANVAS_SIZE = {
  width: 614,
  height: 406,
};

// New layout constants
const PANEL_WIDTH = 18 * 16; // 18rem in pixels
const IMAGE_NAVIGATION_HEIGHT = 88;
const TOOLBAR_EDITOR_SPACING = 16;
const EDITOR_NAVIGATION_SPACING = 16 * 2;
const EDITOR_TOOLBAR_HEIGHT = 72; // toolbar height with margins
const TOOLBAR_MARGINS = 16;

/**
 * The max height of overall editor is 1080px. This includes the editor toolbar and the image navigation.
 * The 926px is the max height of the canvas. Logic: max editor height - editor toolbar height - image navigation height - spacings.
 */
const MAX_CANVAS_AVAILABLE_HEIGHT = 926;

/**
 * useNewResizableCanvas is a composable that is responsible to provide methods that are related to resizing of the canvas
 *   based on the available space on the screen, accounting for the new editor layout with editor toolbar,
 *   collapsible side panel, and image navigation.
 */
export function useNewResizableCanvas() {
  const { isSmallTablet } = useMonitorSize();
  const store = useEditorStore();
  const image = computed(() => store.selectedImage);

  // { [image_id]: { panelOpen: scale, panelClosed: scale } }
  const scaleMap: Record<string, { panelOpen: number; panelClosed: number }> = {};

  /**
   * isCanvasExtendable calculates the available space on the page
   *   and check if the canvas can be extended to a bigger size.
   */
  const isCanvasExtendable = (): boolean => {
    const scale = getScale();

    return scale > THRESHOLD_SCALE && scale <= MAXIMUM_SCALE;
  };

  /**
   * isCanvasShrinkable calculates the available space on the page
   *   and check if the canvas should be shrinked to a smaller size.
   *   For eg: if the height/width of the preview image > screen height/width; Esp. in mobile screens
   */
  const isCanvasShrinkable = (): boolean => getScale() < 1;

  /**
   * getScale calculates the scale that the image can be scaled to
   *   as per the available space on the screen, considering the new layout elements.
   * @param force Defaults to false. When true, will force re-calculate the value and does not use the value from cache
   */
  const getScale = (force = false): number => {
    const imageId = image.value?.meta?.id;
    if (!imageId) return 1;

    // Check if we have cached scale for current panel state
    const isSidePanelOpen = store.openedPanel !== null;
    const panelState = isSidePanelOpen ? "panelOpen" : "panelClosed";

    if (!force && scaleMap[imageId] && scaleMap[imageId][panelState] !== undefined) {
      return scaleMap[imageId][panelState];
    }

    const headerEl = document.getElementById("navbar");

    if (isSmallTablet.value) return scaleForSmallScreen();

    const navHeight = headerEl.clientHeight;

    // Get dimensions of new layout elements
    const editorToolbarEl = document.getElementById("editor-action-toolbar");
    const editorToolbarHeight = editorToolbarEl
      ? editorToolbarEl?.clientHeight + TOOLBAR_MARGINS
      : EDITOR_TOOLBAR_HEIGHT;
    const imageNavigationEl = document.getElementById("image-navigation");
    const imageNavigationHeight = imageNavigationEl?.clientHeight || IMAGE_NAVIGATION_HEIGHT;

    // Check if side panel is open (store.openedPanel !== null means a panel is open)
    const sideMenuWidth = isSidePanelOpen ? PANEL_WIDTH : 0;

    // Calculate available space considering new layout
    const totalPageHeight = document.getElementById("page")?.clientHeight || 0;
    const previewImageHeight = image.value?.meta?.previewHeight || DEFAULT_CANVAS_SIZE.height;

    // Available height = total page height - navbar - editor toolbar - spacing - image navigation - spacing
    const availableHeight =
      totalPageHeight -
      navHeight -
      editorToolbarHeight -
      TOOLBAR_EDITOR_SPACING -
      IMAGE_NAVIGATION_HEIGHT -
      imageNavigationHeight -
      EDITOR_NAVIGATION_SPACING;

    const heightScalingScale = Math.min(availableHeight, MAX_CANVAS_AVAILABLE_HEIGHT) / previewImageHeight;

    const screenWidth = headerEl.clientWidth;
    const previewImageWidth = image.value?.meta?.previewWidth || DEFAULT_CANVAS_SIZE.width;

    // Available width = screen width - side panel (if open) - horizontal spacing
    const horizontalSpacing = 96 * 2; // Same as original for breathing space
    const availableWidth = screenWidth - sideMenuWidth - horizontalSpacing;
    const widthScalingScale = availableWidth / previewImageWidth;

    let scale = Math.min(heightScalingScale, widthScalingScale);
    scale = Math.round((scale + Number.EPSILON) * 1000) / 1000;

    if (scale > MAXIMUM_SCALE) {
      scale = MAXIMUM_SCALE;
    }

    // Initialize cache entry if it doesn't exist
    if (!scaleMap[imageId]) {
      scaleMap[imageId] = { panelOpen: 0, panelClosed: 0 };
    }

    // Cache the scale for current panel state
    scaleMap[imageId][panelState] = scale;

    return scale;
  };

  /**
   * scaleForSmallScreen calculates the scale that the image can be scaled to for small screen
   *   considering the new layout elements.
   */
  const scaleForSmallScreen = () => {
    const navbarHeight = document.getElementById("navbar")?.clientHeight || 0;
    const mobileNavbarEl = document.getElementById("new-header");
    const mobileNavbarHeight = mobileNavbarEl?.clientHeight || 0;
    const footerHeight = document.getElementById("footer")?.clientHeight || 0;
    const mobileDrawerSpaceHeight = document.getElementById("mobileDrawerSpace")?.clientHeight || 0;
    const windowSize = {
      width: window.innerWidth,
      height: window.innerHeight,
    };

    const bottomBar = document.getElementById("mobile-buttons-wrapper");
    const bottomBarSize = {
      width: bottomBar?.clientWidth || 0,
      height: bottomBar?.clientHeight || 0,
    };

    // Average size of the gap between the elements
    const gap = 20;

    const maxEditorHeight = Math.min(
      windowSize.height -
        Math.max(navbarHeight, mobileNavbarHeight) -
        footerHeight -
        bottomBarSize.height -
        TOOLBAR_EDITOR_SPACING -
        EDITOR_NAVIGATION_SPACING -
        9 * gap -
        (store.openedPanel !== null ? mobileDrawerSpaceHeight / 2 : 0),
      image.value?.meta?.previewHeight
    );

    const maxEditorWidth = Math.min(windowSize.width - 28, image.value?.meta?.previewWidth);

    const heightScale = maxEditorHeight / image.value?.meta?.previewHeight;
    const widthScale = maxEditorWidth / image.value?.meta?.previewWidth;
    const scale = Math.round((Math.min(heightScale, widthScale) + Number.EPSILON) * 1000) / 1000;

    // Cache the scale for mobile (same logic as desktop)
    const imageId = image.value?.meta?.id;
    const isMobileSidePanelOpen = store.openedPanel !== null;
    const mobilePanelState = isMobileSidePanelOpen ? "panelOpen" : "panelClosed";

    if (imageId) {
      if (!scaleMap[imageId]) {
        scaleMap[imageId] = { panelOpen: 0, panelClosed: 0 };
      }
      scaleMap[imageId][mobilePanelState] = scale;
    }

    return scale;
  };

  /**
   * This function calculates the new dimensions of the selected image after scaling the image.
   * The scale is calculated by `getScale()`
   */
  const resizedCanvasDimensions = () => {
    let resizedHeight = store.selectedImage?.meta?.previewHeight;
    let resizedWidth = store.selectedImage?.meta?.previewWidth;
    const scale = getScale();

    if (isCanvasExtendable() || isCanvasShrinkable()) {
      resizedHeight = resizedHeight * scale;
      resizedWidth = resizedWidth * scale;
    }

    return {
      height: resizedHeight,
      width: resizedWidth,
      scale: scale,
    };
  };

  /**
   * Clears the scale cache for a specific image or all images
   * @param imageId Optional image ID to clear cache for specific image, if not provided clears all
   */
  const clearScaleCache = (imageId?: string) => {
    if (imageId) {
      delete scaleMap[imageId];
    } else {
      Object.keys(scaleMap).forEach((key) => delete scaleMap[key]);
    }
  };

  return {
    isCanvasExtendable,
    isCanvasShrinkable,
    resizedCanvasDimensions,
    getScale,
    clearScaleCache,
  };
}
